import { memo, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Archive,
  ArrowDown,
  ArrowUp,
  ChevronsUpDown,
  Minus,
  Search,
  ServerCrash,
} from 'lucide-react';
import Fuse from 'fuse.js';
import { cn } from '../../utils/cn';
import { EmptyState } from '../ui/EmptyState';
import { Sparkline } from '../charts/Sparkline';
import { formatPlayers, formatPopularity } from '../../utils/starchart';
import type { PopularityWindow, StarchartServerRow } from '../../types/starchart';

type SortField = 'name' | 'players' | 'popularity' | 'peak';
type SortDirection = 'asc' | 'desc';

interface ServerLeaderboardTableProps {
  rows: StarchartServerRow[];
  popularityWindow: PopularityWindow;
  loading?: boolean;
}

/** Rank movement, coloured by direction. Positive means the server climbed. */
const RankDelta = memo(({ delta }: { delta: number | undefined }) => {
  if (delta === undefined) return null;
  if (delta === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-glass-500" title="No change in 24 hours">
        <Minus className="w-3 h-3" />
      </span>
    );
  }
  const climbed = delta > 0;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 tabular-nums',
        climbed ? 'text-green-400' : 'text-red-400',
      )}
      title={`${climbed ? 'Up' : 'Down'} ${Math.abs(delta)} ${
        Math.abs(delta) === 1 ? 'place' : 'places'
      } in 24 hours`}
    >
      {climbed ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
      {Math.abs(delta)}
    </span>
  );
});
RankDelta.displayName = 'RankDelta';

/** One row. Memoised on the row object so a socket frame for one server does not re-render 58. */
const LeaderboardRow = memo(
  ({ row, popularityWindow }: { row: StarchartServerRow; popularityWindow: PopularityWindow }) => {
    const online = row.live ? !row.live.isOffline : row.catalog?.isServerOnline ?? false;
    const points = useMemo(
      () => row.history.map((p) => ({ t: p.timestamp * 1000, v: p.player_count })),
      [row.history],
    );

    return (
      <tr>
        <td className="td">
          <div className="flex items-center gap-2 min-w-0">
            <span
              className={cn(
                'w-2 h-2 rounded-full shrink-0',
                online ? 'bg-green-400' : 'bg-glass-400',
              )}
              title={online ? 'Online' : 'Offline'}
              aria-label={online ? 'Online' : 'Offline'}
            />
            {row.catalog?.thumbnailUrl && (
              <img
                src={row.catalog.thumbnailUrl}
                alt=""
                loading="lazy"
                className="w-7 h-7 rounded-md object-cover shrink-0"
              />
            )}
            <div className="min-w-0">
              <Link to={`/starchart/${row.apiServerId}`} className="link block truncate">
                {row.displayName}
              </Link>
              <div className="text-xs text-glass-500 font-mono truncate">{row.apiServerId}</div>
            </div>
          </div>
        </td>

        <td className="td">
          <div className="flex flex-wrap items-center gap-1">
            {row.catalog?.regionName && (
              <span className="badge badge-neutral">{row.catalog.regionName}</span>
            )}
            {row.isArchived && (
              <span className="badge badge-orange" title="Tracked by StarChart but no longer on the server list">
                Archived
              </span>
            )}
            {row.isUntracked && (
              <span className="badge badge-info" title="On the server list but StarChart has no history for it">
                No history
              </span>
            )}
            {row.isCaseShadowed && (
              <span
                className="badge badge-warning"
                title="A differently-cased variant of this hostname is tracked separately and carries far more data"
              >
                Case variant
              </span>
            )}
          </div>
        </td>

        <td className="td text-right">
          <span className="tabular-nums font-medium text-white">
            {formatPlayers(row.latestPlayers)}
          </span>
          {row.live && (
            <span className="text-glass-500 tabular-nums"> / {row.live.maxPlayers}</span>
          )}
        </td>

        <td className="td text-right tabular-nums text-glass-300">
          {formatPlayers(row.peakPlayers)}
        </td>

        <td className="td text-right">
          <div className="flex items-center justify-end gap-2">
            <RankDelta delta={row.rankDelta[popularityWindow]} />
            <span className="tabular-nums text-glass-300">
              {formatPopularity(row.popularity[popularityWindow])}
            </span>
            {row.rank[popularityWindow] !== undefined && (
              <span className="badge badge-neutral tabular-nums">#{row.rank[popularityWindow]}</span>
            )}
          </div>
        </td>

        <td className="td">
          <Sparkline points={points} />
        </td>
      </tr>
    );
  },
);
LeaderboardRow.displayName = 'LeaderboardRow';

/**
 * Every tracked server, ranked.
 *
 * Live player counts come from the status socket, popularity and rank from StarChart, and the
 * modpack identity from the PlayCDU server list — the three joined on the FQDN. The sparkline is
 * fed by a single batched history request covering the whole network, not one per row.
 */
export const ServerLeaderboardTable = memo(
  ({ rows, popularityWindow, loading }: ServerLeaderboardTableProps) => {
    const [query, setQuery] = useState('');
    const [showArchived, setShowArchived] = useState(false);
    const [sortField, setSortField] = useState<SortField>('players');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

    const fuse = useMemo(
      () =>
        new Fuse(rows, {
          keys: ['displayName', 'apiServerId', 'catalog.regionName', 'catalog.clusterName'],
          threshold: 0.35,
          ignoreLocation: true,
        }),
      [rows],
    );

    const visible = useMemo(() => {
      const searched = query.trim() ? fuse.search(query.trim()).map((hit) => hit.item) : rows;
      const filtered = showArchived
        ? searched
        : searched.filter((row) => !row.isArchived && !row.isCaseShadowed);

      const direction = sortDirection === 'asc' ? 1 : -1;
      // Numeric fields subtract rather than compare: the popularity index carries fourteen
      // significant digits, so a generic comparator mis-orders near-ties.
      const sorted = [...filtered].sort((a, b) => {
        switch (sortField) {
          case 'name':
            return direction * a.displayName.localeCompare(b.displayName);
          case 'peak':
            // Unknown peaks sort as below zero so they never displace a real measurement.
            return direction * ((a.peakPlayers ?? -1) - (b.peakPlayers ?? -1));
          case 'popularity':
            return (
              direction *
              ((a.popularity[popularityWindow] ?? -1) - (b.popularity[popularityWindow] ?? -1))
            );
          default:
            return direction * (a.latestPlayers - b.latestPlayers);
        }
      });

      return sorted;
    }, [rows, query, fuse, showArchived, sortField, sortDirection, popularityWindow]);

    const archivedCount = rows.filter((row) => row.isArchived || row.isCaseShadowed).length;

    const toggleSort = (field: SortField) => {
      if (sortField === field) {
        setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortField(field);
        setSortDirection(field === 'name' ? 'asc' : 'desc');
      }
    };

    const SortButton = ({ field, label }: { field: SortField; label: string }) => (
      <button
        type="button"
        onClick={() => toggleSort(field)}
        className="inline-flex items-center gap-1 hover:text-white transition-colors"
        title={`Sort by ${label.toLowerCase()}`}
      >
        {label}
        {sortField === field ? (
          sortDirection === 'asc' ? (
            <ArrowUp className="w-3 h-3" />
          ) : (
            <ArrowDown className="w-3 h-3" />
          )
        ) : (
          <ChevronsUpDown className="w-3 h-3 opacity-50" />
        )}
      </button>
    );

    /** A hostname-looking query that matches nothing is still chartable — the id may be archived. */
    const looksLikeHostname = /^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}$/i.test(query.trim());

    return (
      <div className="space-y-3">
        <div className="flex flex-col md:flex-row md:items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
            <input
              type="text"
              className="form-input pl-9"
              placeholder="Search servers, modpacks or regions..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search servers"
            />
          </div>

          <button
            type="button"
            className={cn('glass-button', showArchived && 'border-accent-400')}
            onClick={() => setShowArchived((prev) => !prev)}
            aria-pressed={showArchived}
          >
            <Archive className="w-4 h-4" />
            {showArchived ? 'Hide' : 'Show'} archived
            {archivedCount > 0 && <span className="badge badge-neutral">{archivedCount}</span>}
          </button>

          <span className="text-xs text-glass-400 tabular-nums shrink-0">
            {visible.length} of {rows.length}
          </span>
        </div>

        {visible.length === 0 ? (
          <EmptyState
            icon={ServerCrash}
            title="No servers match"
            description={
              looksLikeHostname
                ? 'StarChart may still hold history for this hostname even though it is not in the list.'
                : 'Try a different search, or show archived servers.'
            }
            action={
              looksLikeHostname ? (
                <Link to={`/starchart/${query.trim()}`} className="btn-primary">
                  Chart {query.trim()} anyway
                </Link>
              ) : undefined
            }
          />
        ) : (
          <div className="table-wrap max-h-[70vh]">
            <table className="table">
              <thead>
                <tr>
                  <th className="th">
                    <SortButton field="name" label="Server" />
                  </th>
                  <th className="th">Tags</th>
                  <th className="th text-right">
                    <SortButton field="players" label="Players" />
                  </th>
                  <th className="th text-right">
                    <SortButton field="peak" label="Peak" />
                  </th>
                  <th className="th text-right">
                    <SortButton field="popularity" label={`Popularity ${popularityWindow}`} />
                  </th>
                  <th className="th">Trend</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => (
                  <LeaderboardRow
                    key={row.apiServerId}
                    row={row}
                    popularityWindow={popularityWindow}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {loading && (
          <p className="text-xs text-glass-500 text-center">Loading history for the sparklines…</p>
        )}
      </div>
    );
  },
);

ServerLeaderboardTable.displayName = 'ServerLeaderboardTable';
